import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ColumnSectionHeader from '../ColumnSectionHeader';
import ProfileSectionTabs from './ProfileSectionTabs';
import ProfileSectionCardGrid from './ProfileSectionCardGrid';
import ProfileStatusLegend from './ProfileStatusLegend';

/**
 * Section 2 always has:
 * - exactly one Company tab
 * - at least one Program tab (from existing Programs only)
 *
 * Not valid: Company only, or Program(s) only.
 * Program tabs are never created here — they mirror Programs.
 */
const ProfileSectionsColumn = ({
  mode,
  definitions = [],
  programs = [],
  sections = [],
  onOpenCard,
  buildError = '',
}) => {
  const isBuilt = mode === 'built';
  const [activeTab, setActiveTab] = useState('company');
  const hasPrograms = programs.length > 0;

  const tabs = useMemo(() => {
    if (!hasPrograms) return [];
    return [
      { id: 'company', label: 'Company' },
      ...programs.map((program) => ({
        id: program._id,
        label: program.productName,
      })),
    ];
  }, [hasPrograms, programs]);

  useEffect(() => {
    if (!hasPrograms) {
      setActiveTab('company');
      return;
    }
    const tabIds = new Set(tabs.map((tab) => tab.id));
    if (!tabIds.has(activeTab)) {
      setActiveTab('company');
    }
  }, [activeTab, hasPrograms, tabs]);

  const activeScope = activeTab === 'company' ? 'company' : 'program';

  const visibleDefinitions = useMemo(
    () =>
      definitions
        .filter((item) => item.scope === activeScope && item.active !== false)
        .sort((a, b) => {
          if (a.role === 'additional_knowledge') return 1;
          if (b.role === 'additional_knowledge') return -1;
          return a.order - b.order;
        }),
    [activeScope, definitions],
  );

  const visibleSections = useMemo(() => {
    const programId = activeTab === 'company' ? null : activeTab;
    return sections.filter((section) => {
      if (section.active === false) return false;
      if (activeTab === 'company') return !section.programId;
      return String(section.programId) === String(programId);
    });
  }, [activeTab, sections]);

  return (
    <Box
      border={1}
      borderColor='divider'
      borderRadius={2}
      p={2.5}
      display='flex'
      flexDirection='column'
      minHeight={{ md: 640 }}
      bgcolor='#fff'
    >
      <ColumnSectionHeader
        number={2}
        title={isBuilt ? 'Review your Company Profile' : "We'll create all of this for you"}
        subtitle={
          isBuilt
            ? 'Open a card to review the points generated from your sources.'
            : 'From your sources, we’ll generate the key information Lead Agents need.'
        }
      />

      {hasPrograms && (
        <ProfileSectionTabs
          tabs={tabs}
          value={tabs.some((tab) => tab.id === activeTab) ? activeTab : 'company'}
          onChange={setActiveTab}
        />
      )}

      {visibleDefinitions.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Section definitions will appear here once available.
        </Typography>
      ) : (
        <>
          {mode === 'generating' && (
            <Typography variant='body2' color='text.secondary' mb={1.5}>
              Building your Company and Product Profiles from the processed sources. This can take a few minutes.
            </Typography>
          )}
          {buildError && (
            <Typography variant='body2' color='error' mb={1.5}>
              {buildError}
            </Typography>
          )}
          <ProfileSectionCardGrid
            definitions={visibleDefinitions}
            sections={isBuilt ? visibleSections : []}
            interactive={isBuilt}
            onOpen={(definition) => onOpenCard(definition, activeTab === 'company' ? null : activeTab)}
          />
        </>
      )}

      <ProfileStatusLegend />
    </Box>
  );
};

export default ProfileSectionsColumn;
